/**
 * Reusable hook for clipboard paste and drag-and-drop file handling.
 * Manages pending files state and provides event handlers for paste, drop, and drag.
 */
import { useState, useCallback, useRef } from 'react';
import { filesFromClipboard } from '../clipboardFiles';

/** Why a file was turned away — a caller can word its own message per reason. */
export type ClipboardRejectReason = 'type' | 'size';

interface UseClipboardPasteOptions {
  /** Max number of files allowed at once (default: 10) */
  maxFiles?: number;
  /** Accepted MIME type prefixes or exact types. Empty = accept all. */
  acceptedTypes?: string[];
  /** Max size per file in MB. Omit for no client-side size limit. */
  maxSizeMB?: number;
  /** Called when a file is rejected (type mismatch or over the size limit) */
  onReject?: (file: File, reason: ClipboardRejectReason) => void;
}

interface UseClipboardPasteReturn {
  /** Files pending upload */
  pendingFiles: File[];
  /** Handle paste event — attach to onPaste */
  handlePaste: (e: React.ClipboardEvent) => void;
  /** Handle drop event — attach to onDrop */
  handleDrop: (e: React.DragEvent) => void;
  /** Handle drag enter — attach to onDragEnter */
  handleDragEnter: (e: React.DragEvent) => void;
  /** Handle drag leave — attach to onDragLeave */
  handleDragLeave: (e: React.DragEvent) => void;
  /** Handle drag over (prevents default) — attach to onDragOver */
  handleDragOver: (e: React.DragEvent) => void;
  /** Add files from a file input change event */
  handleFileInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  /** Remove a pending file by index */
  removePendingFile: (index: number) => void;
  /** Clear all pending files */
  clearPendingFiles: () => void;
  /** Whether a drag is currently over the drop target */
  isDragging: boolean;
}

function isAcceptedType(file: File, acceptedTypes: string[]): boolean {
  if (acceptedTypes.length === 0) return true;
  return acceptedTypes.some((accepted) => {
    if (accepted.endsWith('/*')) {
      // Wildcard prefix like "image/*"
      return file.type.startsWith(accepted.replace('/*', '/'));
    }
    return file.type === accepted;
  });
}

export function useClipboardPaste(options: UseClipboardPasteOptions = {}): UseClipboardPasteReturn {
  const { maxFiles = 10, acceptedTypes = [], maxSizeMB, onReject } = options;
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const dragCounterRef = useRef(0);

  const addFiles = useCallback((files: File[]) => {
    const accepted: File[] = [];
    for (const file of files) {
      if (acceptedTypes.length > 0 && !isAcceptedType(file, acceptedTypes)) {
        onReject?.(file, 'type');
        continue;
      }
      if (maxSizeMB !== undefined && file.size > maxSizeMB * 1024 * 1024) {
        onReject?.(file, 'size');
        continue;
      }
      accepted.push(file);
    }
    if (accepted.length === 0) return;
    setPendingFiles((prev) => {
      const combined = [...prev, ...accepted];
      return combined.slice(0, maxFiles);
    });
  }, [acceptedTypes, maxFiles, maxSizeMB, onReject]);

  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    // Single-sourced with every other paste-to-attach surface (clipboardFiles):
    // a clipboard carrying text flavours is a rich-text paste that merely ships a
    // bitmap RENDITION of the selection — Word, Excel and Sheets all do it — and
    // claiming it would attach a picture of a spreadsheet on every formatted
    // paste. It also names a generic `image.png` screenshot readably.
    const files = filesFromClipboard(e.clipboardData);
    if (files.length > 0) {
      e.preventDefault();
      addFiles(files);
    }
  }, [addFiles]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    dragCounterRef.current = 0;

    const files = e.dataTransfer?.files;
    if (!files?.length) return;
    addFiles(Array.from(files));
  }, [addFiles]);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    dragCounterRef.current += 1;
    if (dragCounterRef.current === 1) {
      setIsDragging(true);
    }
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    dragCounterRef.current -= 1;
    if (dragCounterRef.current === 0) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      addFiles(Array.from(files));
    }
    // Reset so the same file can be re-selected
    e.target.value = '';
  }, [addFiles]);

  const removePendingFile = useCallback((index: number) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const clearPendingFiles = useCallback(() => {
    setPendingFiles([]);
  }, []);

  return {
    pendingFiles,
    handlePaste,
    handleDrop,
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
    handleFileInputChange,
    removePendingFile,
    clearPendingFiles,
    isDragging,
  };
}
